export const getGoalBalances = (goals = [], savingsLedger = []) => {
  const balances = new Map((goals || []).map((goal) => [goal.id, 0]));

  (savingsLedger || []).forEach((entry) => {
    if (!entry.goal_id) return;

    const current = balances.get(entry.goal_id) || 0;
    const amount = Number(entry.amount_cents) || 0;

    if (entry.direction === 'deposit') {
      balances.set(entry.goal_id, current + amount);
    } else if (entry.direction === 'withdraw' || entry.direction === 'withdrawal') {
      balances.set(entry.goal_id, current - amount);
    }
  });

  return balances;
};

const getTimestamp = (item) => {
  const value = new Date(item?.created_at || 0).getTime();
  return Number.isNaN(value) ? 0 : value;
};

/**
 * Build one kid-friendly activity stream from transactions + savings ledger.
 *
 * The important accounting rule is the same rule used by useBalance():
 *
 *   Available Money
 *   = approved transaction balance - money currently held in savings
 *
 * We calculate the history oldest -> newest, attach the Available Money that
 * existed immediately AFTER each event, then return the visible activities
 * newest -> oldest for the dashboard.
 *
 * No balance is stored in the database. It is reconstructed from the existing
 * accounting records so there is no second balance field that can drift out of
 * sync.
 */
export const buildUnifiedActivity = ({
  transactions = [],
  savingsLedger = [],
  goals = [],
}) => {
  const goalMap = new Map((goals || []).map((goal) => [goal.id, goal]));

  const timeline = [];

  // --------------------------------------------------
  // Normal transactions
  // --------------------------------------------------

  (transactions || []).forEach((transaction, index) => {
    const amount = Number(transaction.amount_cents) || 0;
    const isApproved = transaction.approval_status === 'approved';

    let availableDelta = 0;

    if (isApproved) {
      if (transaction.type === 'income') {
        availableDelta = amount;
      } else if (transaction.type === 'expense') {
        availableDelta = -amount;
      }
    }

    timeline.push({
      ...transaction,
      activity_kind: 'transaction',
      activity_id: `txn-${transaction.id}`,
      available_delta_cents: availableDelta,
      _timeline_priority: 30,
      _timeline_index: index,
      _hidden_from_activity: false,
    });
  });

  // --------------------------------------------------
  // Savings ledger
  // --------------------------------------------------

  const seenTransfers = new Set();

  (savingsLedger || []).forEach((entry, index) => {
    const amount = Number(entry.amount_cents) || 0;
    const activityType =
      entry.activity_type ||
      (entry.direction === 'deposit' ? 'deposit' : 'withdrawal');

    const goal = goalMap.get(entry.goal_id);
    const relatedGoal = goalMap.get(entry.related_goal_id);

    // ------------------------------------------------
    // Goal completion
    // ------------------------------------------------
    // Completing a goal produces TWO accounting records:
    //   1. savings withdrawal (money leaves the goal)
    //   2. expense transaction (the saved money is spent)
    //
    // We hide the ledger row so the child sees only one activity item, but we
    // MUST keep its +Available Money effect in the historical calculation.
    // Otherwise the completion expense would incorrectly reduce Available
    // Money a second time.

    if (activityType === 'complete') {
      timeline.push({
        ...entry,
        activity_kind: 'savings',
        activity_id: `savings-complete-${entry.id}`,
        savings_activity_type: 'complete',
        goal_name: goal?.name || 'Savings Goal',
        available_delta_cents: amount,
        _timeline_priority: 10,
        _timeline_index: index,
        _hidden_from_activity: true,
      });

      return;
    }

    // ------------------------------------------------
    // Goal-to-goal transfer
    // ------------------------------------------------
    // A transfer changes which goal holds the money, but it does NOT change
    // total savings and therefore does NOT change Available Money.

    if (activityType === 'transfer') {
      if (!entry.transfer_id || seenTransfers.has(entry.transfer_id)) return;

      const transferRows = (savingsLedger || []).filter(
        (candidate) => candidate.transfer_id === entry.transfer_id
      );

      const sourceRow =
        transferRows.find(
          (candidate) =>
            candidate.direction === 'withdraw' ||
            candidate.direction === 'withdrawal'
        ) || entry;

      // Only create the visible transfer from the source row.
      if (entry.id !== sourceRow.id) return;

      seenTransfers.add(entry.transfer_id);

      const sourceGoal = goalMap.get(sourceRow.goal_id);
      const destinationGoal = goalMap.get(sourceRow.related_goal_id);

      timeline.push({
        ...sourceRow,
        activity_kind: 'savings',
        activity_id: `savings-transfer-${entry.transfer_id}`,
        savings_activity_type: 'transfer',
        goal_name: sourceGoal?.name || 'Savings Goal',
        related_goal_name: destinationGoal?.name || 'Another Goal',
        display_title: 'Moved Between Goals',
        display_subtitle: `${sourceGoal?.name || 'Savings Goal'} → ${destinationGoal?.name || 'Another Goal'}`,
        amount_label: 'Transferred',
        display_sign: '',
        available_delta_cents: 0,
        _timeline_priority: 20,
        _timeline_index: index,
        _hidden_from_activity: false,
      });

      return;
    }

    // ------------------------------------------------
    // Deposit / withdrawal / cancellation
    // --------------------------------------------------

    let displayTitle = 'Savings Activity';
    let amountLabel = 'Savings change';
    let displaySign = '';
    let availableDelta = 0;

    if (activityType === 'deposit') {
      displayTitle = `Moved to Goal • ${goal?.name || 'Savings Goal'}`;
      amountLabel = 'Moved to goal';
      displaySign = '';
      availableDelta = -amount;
    } else if (activityType === 'cancel') {
      displayTitle = `Goal Cancelled • ${goal?.name || 'Savings Goal'}`;
      amountLabel = 'Returned to Available Money';
      displaySign = '+';
      availableDelta = amount;
    } else {
      displayTitle = `Returned from Goal • ${goal?.name || 'Savings Goal'}`;
      amountLabel = 'Returned to Available Money';
      displaySign = '+';
      availableDelta = amount;
    }

    timeline.push({
      ...entry,
      activity_kind: 'savings',
      activity_id: `savings-${entry.id}`,
      savings_activity_type: activityType,
      goal_name: goal?.name || 'Savings Goal',
      display_title: displayTitle,
      amount_label: amountLabel,
      display_sign: displaySign,
      available_delta_cents: availableDelta,
      _timeline_priority: 20,
      _timeline_index: index,
      _hidden_from_activity: false,
    });
  });

  // --------------------------------------------------
  // Calculate Available Money after each activity
  // --------------------------------------------------

  timeline.sort((a, b) => {
    const timestampDifference = getTimestamp(a) - getTimestamp(b);

    if (timestampDifference !== 0) {
      return timestampDifference;
    }

    // Goal-completion savings withdrawal must be processed before the
    // companion expense when timestamps are identical.
    if (a._timeline_priority !== b._timeline_priority) {
      return a._timeline_priority - b._timeline_priority;
    }

    return a._timeline_index - b._timeline_index;
  });

  let availableMoney = 0;

  timeline.forEach((activity) => {
    availableMoney += Number(activity.available_delta_cents) || 0;
    activity.available_balance_after_cents = availableMoney;
  });

  // Keep internal helper fields out of the objects consumed by the UI.
  return timeline
    .filter((activity) => !activity._hidden_from_activity)
    .map((activity) => {
      const {
        _timeline_priority,
        _timeline_index,
        _hidden_from_activity,
        ...visibleActivity
      } = activity;

      return visibleActivity;
    })
    .sort((a, b) => getTimestamp(b) - getTimestamp(a));
};


// ==================================================
// Kid Report Helpers
// ==================================================

/**
 * Convert a timestamp to the calendar date seen by the child in the browser.
 * YYYY-MM-DD strings sort correctly, which makes inclusive date filtering
 * simple and avoids accidental UTC date shifts around midnight.
 */
export const getLocalDateKey = (value) => {
  const date = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(date.getTime())) return '';

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

export const getRollingDateRange = (days, today = new Date()) => {
  const safeDays = Math.max(1, Number(days) || 1);

  const end = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );

  const start = new Date(end);
  start.setDate(start.getDate() - (safeDays - 1));

  return {
    startDate: getLocalDateKey(start),
    endDate: getLocalDateKey(end),
  };
};

export const filterActivityByDateRange = (
  activities = [],
  startDate,
  endDate
) => {
  if (!startDate || !endDate) return [];

  return (activities || []).filter((activity) => {
    const activityDate = getLocalDateKey(activity.created_at);

    return (
      activityDate &&
      activityDate >= startDate &&
      activityDate <= endDate
    );
  });
};

export const getActivityReportSummary = (activities = []) => {
  return (activities || []).reduce(
    (summary, activity) => {
      summary.activityCount += 1;

      if (activity.activity_kind === 'transaction') {
        const amount = Number(activity.amount_cents) || 0;
        const isApproved = activity.approval_status === 'approved';

        if (!isApproved) return summary;

        if (activity.type === 'income') {
          summary.moneyAddedCents += amount;

          if (activity.category === 'Chores') {
            summary.choreEarningsCents += amount;
          }
        } else if (activity.type === 'expense') {
          summary.moneySpentCents += amount;
        }

        return summary;
      }

      if (
        activity.activity_kind === 'savings' &&
        activity.savings_activity_type === 'deposit'
      ) {
        summary.movedToGoalsCents +=
          Number(activity.amount_cents) || 0;
      }

      return summary;
    },
    {
      moneyAddedCents: 0,
      moneySpentCents: 0,
      movedToGoalsCents: 0,
      choreEarningsCents: 0,
      activityCount: 0,
    }
  );
};
